/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2 } from 'lucide-react';
import LeakChip from '@/components/terra/LeakChip';
import EcosystemBadge from '@/components/terra/EcosystemBadge';
import MovementPathCard from '@/components/terra/MovementPathCard';

/**
 * Perception Leakage Detection™ — dedicated weekly view.
 *
 * Calm rhythm. Counselors check this on their own cadence. Never urgent-signal framing.
 */
export default function PerceptionLeakagePage() {
  const [data, setData] = useState(null);
  const [ecosystems, setEcosystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [activeLead, setActiveLead] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/terra-growth/intelligence/leakage`),
      axios.get(`${API}/terra-growth/ecosystems`),
    ])
      .then(([d, e]) => { setData(d.data); setEcosystems(e.data?.ecosystems || []); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const ecoMap = useMemo(() => ecosystems.reduce((m, e) => { m[e.code] = e; return m; }, {}), [ecosystems]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === 'ALL') return data.leaks;
    return data.leaks.filter(l => l.categories.includes(filter));
  }, [data, filter]);

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="perception-leakage-page" className="max-w-7xl mx-auto px-6 py-8">
        <header className="mb-6">
          <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239]">Terra Growth OS™ · Intelligence</p>
          <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-1">Perception Leakage Detection™</h1>
          <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-1 max-w-2xl">
            Calm weekly rhythm. Surfaces leads who quietly need movement, deeper follow-up,
            family alignment, or decision clarity. Never urgent-signal framing — pick your moment.
          </p>
        </header>

        {loading ? (
          <Skeleton />
        ) : !data ? (
          <Skeleton message="Could not load leakage view." />
        ) : (
          <>
            <CategoryStrip data={data} filter={filter} onFilter={setFilter} />
            {filtered.length === 0 ? (
              <Empty />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <ul data-testid="leakage-list" className="lg:col-span-2 space-y-2">
                  {filtered.map(l => (
                    <LeakRow key={l.lead_id} l={l} ecoMap={ecoMap}
                              onSelect={() => setActiveLead(l)}
                              active={activeLead?.lead_id === l.lead_id} />
                  ))}
                </ul>
                <aside className="lg:col-span-1">
                  {activeLead
                    ? <LeakDetail lead={activeLead} ecoMap={ecoMap} />
                    : (
                      <div className="rounded-2xl border border-[#EBE8E3] bg-white p-6 text-center text-[#8A8681] text-xs">
                        Select a lead to see suggested movement path.
                      </div>
                    )}
                </aside>
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

function Skeleton({ message }) {
  return (
    <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
      {message ? message : <><Loader2 className="w-5 h-5 mx-auto animate-spin" /> Computing…</>}
    </div>
  );
}
function Empty() {
  return (
    <div data-testid="leakage-empty" className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
      Nothing surfacing right now. Calm week.
    </div>
  );
}

function CategoryStrip({ data, filter, onFilter }) {
  const cats = data.leak_categories || [];
  const counts = data.by_category || {};
  return (
    <div data-testid="leakage-category-strip" className="flex flex-wrap items-center gap-2 mb-4">
      <button data-testid="leak-filter-ALL" onClick={() => onFilter('ALL')}
              className={`text-[11px] rounded-full px-3 py-1 border ${
                filter === 'ALL' ? 'bg-[#4A5D4E] text-white border-[#4A5D4E]' : 'bg-white text-[#5C5955] border-[#D8D4CE]'}`}>
        All ({data.leak_count})
      </button>
      {cats.map(c => (
        <button key={c.code} data-testid={`leak-filter-${c.code}`}
                onClick={() => onFilter(c.code)}
                className={`text-[11px] rounded-full px-3 py-1 border ${
                  filter === c.code ? 'bg-[#4A5D4E] text-white border-[#4A5D4E]' : 'bg-white text-[#5C5955] border-[#D8D4CE]'}`}>
          {c.label} ({counts[c.code] || 0})
        </button>
      ))}
    </div>
  );
}

function LeakRow({ l, ecoMap, onSelect, active }) {
  return (
    <li>
      <button data-testid={`leak-row-${l.lead_id}`} onClick={onSelect}
              className={`w-full text-left rounded-2xl border p-3 transition-all ${
                active ? 'border-[#4A5D4E] bg-[#F4F2EE]' : 'border-[#EBE8E3] bg-white hover:bg-[#FBFaf9]'}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm text-[#2C2A29] truncate">{l.lead_name || '—'}</div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.14em] bg-[#F4F2EE] text-[#5C5955] rounded-full px-2 py-0.5">
                {l.perception_state}
              </span>
              {ecoMap[l.ecosystem_code] && <EcosystemBadge ecosystem={ecoMap[l.ecosystem_code]} compact />}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {l.categories.map(c => <LeakChip key={c} code={c} />)}
        </div>
      </button>
    </li>
  );
}

function LeakDetail({ lead, ecoMap }) {
  const path = { steps: lead.suggested_movement_path, is_curated: true };
  return (
    <div data-testid="leakage-detail" className="rounded-2xl border border-[#EBE8E3] bg-white p-4 space-y-2 sticky top-20">
      <div className="text-sm text-[#2C2A29]">{lead.lead_name}</div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[10px] uppercase tracking-[0.14em] bg-[#F4F2EE] text-[#5C5955] rounded-full px-2 py-0.5">
          {lead.perception_state}
        </span>
        {ecoMap[lead.ecosystem_code] && <EcosystemBadge ecosystem={ecoMap[lead.ecosystem_code]} compact />}
      </div>
      <div className="flex flex-wrap gap-1 pt-1">
        {lead.categories.map(c => <LeakChip key={c} code={c} />)}
      </div>
      <p className="text-[10px] text-[#8A8681]">
        Last conversation depth: <span className="text-[#5C5955]">{lead.last_conversation_depth || '—'}</span>
      </p>
      <MovementPathCard path={path} />
    </div>
  );
}
